(() => {
  var __defProp = Object.defineProperty;
  var __typeError = (msg) => {
    throw TypeError(msg);
  };
  var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
  var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
  var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
  var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
  var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
  var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

  // src/scripts/_base/utilities.js
  var requestIdleCallback = typeof window.requestIdleCallback == "function" ? window.requestIdleCallback : setTimeout;
  var viewTransition = {
    current: void 0
  };
  function fetchConfig(type = "json", config = {}) {
    const headers = { "Content-Type": "application/json", Accept: `application/${type}`, ...config.headers };
    if (type === "javascript") {
      headers["X-Requested-With"] = "XMLHttpRequest";
      delete headers["Content-Type"];
    }
    return {
      method: "POST",
      headers: (
        /** @type {HeadersInit} */
        headers
      ),
      body: config.body
    };
  }
  function debounce(fn, wait) {
    let timeout;
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }
    debounced.cancel = () => {
      clearTimeout(timeout);
    };
    return (
      /** @type {T & { cancel(): void }} */
      debounced
    );
  }
  var reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  function prefersReducedMotion() {
    return reducedMotion.matches;
  }
  function onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements) ? elements.flatMap((element) => element.getAnimations(options)) : elements.getAnimations(options);
    const animationPromises = animations.reduce(
      (acc, animation) => {
        if (animation.timeline instanceof DocumentTimeline) {
          acc.push(animation.finished);
        }
        return acc;
      },
      /** @type {Promise<Animation>[]} */
      []
    );
    return Promise.allSettled(animationPromises).then(callback);
  }
  function isClickedOutside(event, element) {
    if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
      return !isPointWithinElement(event.clientX, event.clientY, element);
    }
    return !element.contains(event.target);
  }
  function isPointWithinElement(x, y, element) {
    const { left, right, top, bottom } = element.getBoundingClientRect();
    return x >= left && x <= right && y >= top && y <= bottom;
  }
  var mediaQueryLarge = matchMedia("(min-width: 750px)");
  function parseIntOrDefault(value, defaultValue) {
    if (value === null || value === void 0 || value === "") {
      return defaultValue;
    }
    const parsed = parseInt(value.toString());
    return isNaN(parsed) ? defaultValue : parsed;
  }
  var _queue, _scheduled;
  var Scheduler = class {
    constructor() {
      /** @type {Set<() => void>} */
      __privateAdd(this, _queue, /* @__PURE__ */ new Set());
      /** @type {boolean} */
      __privateAdd(this, _scheduled, false);
      /** @param {() => void} task */
      __publicField(this, "schedule", async (task) => {
        __privateGet(this, _queue).add(task);
        if (!__privateGet(this, _scheduled)) {
          __privateSet(this, _scheduled, true);
          if (viewTransition.current) await viewTransition.current;
          requestAnimationFrame(this.flush);
        }
      });
      __publicField(this, "flush", () => {
        for (const task of __privateGet(this, _queue)) {
          setTimeout(task, 0);
        }
        __privateGet(this, _queue).clear();
        __privateSet(this, _scheduled, false);
      });
    }
  };
  _queue = new WeakMap();
  _scheduled = new WeakMap();
  var scheduler = new Scheduler();
  if (typeof Theme !== "undefined") {
    Theme.utilities = {
      ...Theme.utilities,
      scheduler
    };
  }

  // src/scripts/_base/component.js
  var DeclarativeShadowElement = class extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const template = this.querySelector(':scope > template[shadowrootmode="open"]');
        if (!(template instanceof HTMLTemplateElement)) return;
        const shadow = this.attachShadow({ mode: "open" });
        shadow.append(template.content.cloneNode(true));
      }
    }
  };
  var _Component_instances, updateRefs_fn, _mutationObserver, _isDescendant;
  var Component = class extends DeclarativeShadowElement {
    constructor() {
      super(...arguments);
      __privateAdd(this, _Component_instances);
      /**
       * An object holding references to child elements with `ref` attributes.
       *
       * @type {RefsType<T>}
       */
      __publicField(
        this,
        "refs",
        /** @type {RefsType<T>} */
        {}
      );
      /**
       * An array of required refs. If a ref is not found, an error will be thrown.
       *
       * @type {string[] | undefined}
       */
      __publicField(this, "requiredRefs");
      /**
       * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
       *
       * @type {MutationObserver}
       */
      __privateAdd(this, _mutationObserver, new MutationObserver((mutations) => {
        if (mutations.some(
          (m) => m.type === "attributes" && __privateGet(this, _isDescendant).call(this, m.target) || m.type === "childList" && [...m.addedNodes, ...m.removedNodes].some(__privateGet(this, _isDescendant))
        )) {
          __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
        }
      }));
      /**
       * Checks if a given node is a descendant of this component.
       *
       * @param {Node} node - The node to check.
       * @returns {boolean} True if the node is a descendant of this component.
       */
      __privateAdd(this, _isDescendant, (node) => getClosestComponent(getAncestor(node)) === this);
    }
    /**
     * Gets the root node of the component, which is either its shadow root or the component itself.
     *
     * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
     */
    get roots() {
      return this.shadowRoot ? [this, this.shadowRoot] : [this];
    }
    /**
     * Called when the element is connected to the document's DOM.
     *
     * Initializes event listeners and refs.
     */
    connectedCallback() {
      super.connectedCallback();
      registerEventListeners();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
      this.loggerColor = "#5983BD";
      requestIdleCallback(() => {
        for (const root of this.roots) {
          __privateGet(this, _mutationObserver).observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["ref"],
            attributeOldValue: true
          });
        }
      });
    }
    /**
     * Called when the element is re-rendered by the Section Rendering API.
     */
    updatedCallback() {
      __privateGet(this, _mutationObserver).takeRecords();
      __privateMethod(this, _Component_instances, updateRefs_fn).call(this);
    }
    /**
     * Called when the element is disconnected from the document's DOM.
     *
     * Disconnects the mutation observer.
     */
    disconnectedCallback() {
      __privateGet(this, _mutationObserver).disconnect();
    }
    /**
     * Prints the given arguments if the component is currently in debug mode;
     * @param  {...any} args 
     */
    log(...args) {
      if (this.debug) {
        console.log(`%c[${this.tagName}]`, `color: ${this.loggerColor}`, ...args);
      }
    }
  };
  _Component_instances = new WeakSet();
  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  updateRefs_fn = function() {
    var _a, _b;
    const refs = (
      /** @type any */
      {}
    );
    const elements = this.roots.reduce(
      (acc, root) => {
        for (const element of root.querySelectorAll("[ref]")) {
          if (!__privateGet(this, _isDescendant).call(this, element)) continue;
          acc.add(element);
        }
        return acc;
      },
      /** @type {Set<Element>} */
      /* @__PURE__ */ new Set()
    );
    for (const ref of elements) {
      const refName = (_a = ref.getAttribute("ref")) != null ? _a : "";
      const isArray = refName.endsWith("[]");
      const path = isArray ? refName.slice(0, -2) : refName;
      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];
        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }
    if ((_b = this.requiredRefs) == null ? void 0 : _b.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }
    this.refs = /** @type {RefsType<T>} */
    refs;
  };
  _mutationObserver = new WeakMap();
  _isDescendant = new WeakMap();
  function getAncestor(node) {
    if (node.parentNode) return node.parentNode;
    const root = node.getRootNode();
    if (root instanceof ShadowRoot) return root.host;
    return null;
  }
  function getClosestComponent(node) {
    if (!node) return null;
    if (node instanceof Component) return node;
    if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith("-component")) return node;
    const ancestor = getAncestor(node);
    if (ancestor) return getClosestComponent(ancestor);
    return null;
  }
  var initialized = false;
  function registerEventListeners() {
    if (initialized) return;
    initialized = true;
    const events = ["click", "change", "select", "focus", "blur", "submit", "input", "keydown", "keyup", "toggle"];
    const shouldBubble = ["focus", "blur"];
    const expensiveEvents = ["pointerenter", "pointerleave"];
    for (const eventName of [...events, ...expensiveEvents]) {
      const attribute = `on:${eventName}`;
      document.addEventListener(
        eventName,
        (event) => {
          var _a;
          const element = getElement(event);
          if (!element) return;
          const proxiedEvent = event.target !== element ? new Proxy(event, {
            get(target, property) {
              if (property === "target") return element;
              const value2 = Reflect.get(target, property);
              if (typeof value2 === "function") {
                return value2.bind(target);
              }
              return value2;
            }
          }) : event;
          const value = (_a = element.getAttribute(attribute)) != null ? _a : "";
          let [selector, method] = value.split("/");
          const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
          const data = matches ? matches[2] : null;
          const instance = selector ? selector.startsWith("#") ? document.querySelector(selector) : element.closest(selector) : getClosestComponent(element);
          if (!(instance instanceof Component) || !method) return;
          method = method.replace(/\?.*/, "");
          const callback = (
            /** @type {any} */
            instance[method]
          );
          if (typeof callback === "function") {
            try {
              const args = [proxiedEvent];
              if (data) args.unshift(parseData(data));
              callback.call(instance, ...args);
            } catch (error2) {
              console.error(error2);
            }
          }
        },
        { capture: true }
      );
    }
    function getElement(event) {
      var _a, _b;
      const target = (_b = (_a = event.composedPath) == null ? void 0 : _a.call(event)[0]) != null ? _b : event.target;
      if (!(target instanceof Element)) return;
      if (target.hasAttribute(`on:${event.type}`)) {
        return target;
      }
      if (expensiveEvents.includes(event.type)) {
        return null;
      }
      return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
    }
  }
  function parseData(str) {
    const delimiter = str[0];
    const data = str.slice(1);
    return delimiter === "?" ? Object.fromEntries(
      Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
    ) : parseValue(data);
  }
  function parseValue(str) {
    if (str === "true") return true;
    if (str === "false") return false;
    const maybeNumber = Number(str);
    if (!isNaN(maybeNumber) && str.trim() !== "") return maybeNumber;
    return str;
  }
  var MissingRefError = class extends Error {
    /**
     * @param {string} ref
     * @param {Component} component
     */
    constructor(ref, component) {
      super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
    }
  };

  // src/scripts/_base/events.js
  var ThemeEvents = class {
  };
  /** @static @constant {string} Event triggered when a variant is selected */
  __publicField(ThemeEvents, "variantSelected", "variant:selected");
  /** @static @constant {string} Event triggered when a variant is changed */
  __publicField(ThemeEvents, "variantUpdate", "variant:update");
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  __publicField(ThemeEvents, "cartUpdate", "cart:update");
  /** @static @constant {string} Event triggered when a cart update fails */
  __publicField(ThemeEvents, "cartError", "cart:error");
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  __publicField(ThemeEvents, "mediaStartedPlaying", "media:started-playing");
  // Event triggered when quantity-selector value is changed
  __publicField(ThemeEvents, "quantitySelectorUpdate", "quantity-selector:update");
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  __publicField(ThemeEvents, "megaMenuHover", "megaMenu:hover");
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  __publicField(ThemeEvents, "zoomMediaSelected", "zoom-media:selected");
  /** @static @constant {string} Event triggered when a discount is applied */
  __publicField(ThemeEvents, "discountUpdate", "discount:update");
  /** @static @constant {string} Event triggered when changing collection filters */
  __publicField(ThemeEvents, "FilterUpdate", "filter:update");
  var _CartAddEvent = class _CartAddEvent extends Event {
    /**
     * Creates a new CartAddEvent
     * @param {Object} [resource] - The new cart object
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {string} [data.variantId] - The id of the product variant that was added
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, data) {
      super(_CartAddEvent.eventName, { bubbles: true });
      this.detail = {
        resource,
        data: {
          ...data
        }
      };
    }
  };
  __publicField(_CartAddEvent, "eventName", ThemeEvents.cartUpdate);
  var CartAddEvent = _CartAddEvent;
  var CartUpdateEvent = class extends Event {
    /**
     * Creates a new CartUpdateEvent
     * @param {Object} resource - The new cart object
     * @param {Object} [data] - Additional event data
     * @param {boolean} [data.didError] - Whether the cart operation failed
     * @param {string} [data.source] - The source of the cart update
     * @param {string} [data.productId] - The id of the product card that was updated
     * @param {string} [data.variantId] - The id of the product variant that was updated
     * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
     */
    constructor(resource, data) {
      super(ThemeEvents.cartUpdate, { bubbles: true });
      this.detail = {
        resource,
        data: {
          ...data
        }
      };
    }
  };
  var QuantitySelectorUpdateEvent = class extends Event {
    /**
     * Creates a new QuantitySelectorUpdateEvent
     * @param {number} quantity - New quantity value
     * @param {number} [cartLine] - The id of the updated cart line
     * @param {number} quantity - Original quantity value
     */
    constructor(quantity, cartLine, originalQuantity) {
      super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
      this.detail = {
        quantity,
        cartLine,
        originalQuantity
      };
    }
  };
  var DiscountUpdateEvent = class extends Event {
    /**
     * Creates a new DiscountUpdateEvent
     * @param {Object} resource - The new cart object
     * @param {Object} [data] - Additional event data
     */
    constructor(resource, data) {
      super(ThemeEvents.discountUpdate, { bubbles: true });
      this.detail = {
        resource,
        data: {
          ...data
        }
      };
    }
  };

  // src/scripts/cart/components/cart-discount.js
  var _activeFetch, _cartDrawer, _debouncedOnChange, _CartDiscount_instances, createAbortController_fn, handleDiscountError_fn, existingDiscounts_fn;
  var CartDiscount = class extends Component {
    constructor() {
      super(...arguments);
      __privateAdd(this, _CartDiscount_instances);
      __publicField(this, "requiredRefs", ["cartDiscountError", "cartDiscountErrorDiscountCode", "cartDiscountErrorShipping"]);
      /** @type {AbortController | null} */
      __privateAdd(this, _activeFetch, null);
      /** @type { CartDrawer | null} */
      __privateAdd(this, _cartDrawer, this.closest("cart-drawer-component"));
      __privateAdd(this, _debouncedOnChange, debounce(this.updateSubmit, 200).bind(this));
      /**
       * Handles updates to the cart note.
       * @param {SubmitEvent} event - The submit event on our form.
       */
      __publicField(this, "applyDiscount", async (event) => {
        this.log("apply discount");
        const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
        event.preventDefault();
        event.stopPropagation();
        const form = event.target;
        this.log("form target", form);
        if (!(form instanceof HTMLFormElement)) return;
        const discountCode = form.querySelector('input[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement)) return;
        const discountCodeValue = discountCode.value;
        const abortController = __privateMethod(this, _CartDiscount_instances, createAbortController_fn).call(this);
        const discountSubmit = form.querySelector(".cart-discount__button");
        discountSubmit == null ? void 0 : discountSubmit.setAttribute("disabled", true);
        __privateGet(this, _cartDrawer).disableCheckout();
        try {
          const existingDiscounts = __privateMethod(this, _CartDiscount_instances, existingDiscounts_fn).call(this);
          this.log("existing Discounts", existingDiscounts);
          if (existingDiscounts.includes(discountCodeValue)) return;
          cartDiscountError.classList.add("hidden");
          cartDiscountErrorDiscountCode.classList.add("hidden");
          cartDiscountErrorShipping.classList.add("hidden");
          const config = fetchConfig("json", {
            body: JSON.stringify({
              discount: [...existingDiscounts, discountCodeValue].join(","),
              sections: __privateGet(this, _cartDrawer).getSectionsToRender()
            })
          });
          const response = await fetch(Theme.routes.cart_update_url, {
            ...config,
            signal: abortController.signal
          });
          const data = await response.json();
          if (data.discount_codes.find((discount) => {
            return discount.code === discountCodeValue && discount.applicable === false;
          })) {
            discountCode.value = "";
            __privateMethod(this, _CartDiscount_instances, handleDiscountError_fn).call(this, "discount_code");
            return;
          }
          const newHtml = data.sections[this.dataset.sectionId];
          const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
          const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
          const discountCodes = (section == null ? void 0 : section.querySelectorAll(".cart-discount__discount-button")) || [];
          if (section) {
            const codes = Array.from(discountCodes).map((element) => element instanceof HTMLLIElement ? element.dataset.discountCode : null).filter(Boolean);
            if (codes.length === existingDiscounts.length && codes.every((code) => existingDiscounts.includes(code)) && data.discount_codes.find((discount) => {
              return discount.code === discountCodeValue && discount.applicable === true;
            })) {
              __privateMethod(this, _CartDiscount_instances, handleDiscountError_fn).call(this, "shipping");
              discountCode.value = "";
              return;
            }
          }
          document.dispatchEvent(new DiscountUpdateEvent(data, { source: "cart-discount-component", sections: data.sections }));
        } catch (error2) {
        } finally {
          __privateSet(this, _activeFetch, null);
          __privateGet(this, _cartDrawer).enableCheckout();
        }
      });
      /**
       * Handles removing a discount from the cart.
       * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
       */
      __publicField(this, "removeDiscount", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.log("remove discount");
        if (event instanceof KeyboardEvent && event.key !== "Enter" || !(event instanceof MouseEvent) || !(event.target instanceof HTMLElement)) {
          this.log("not correct event type");
          return;
        }
        const pill = event.target.closest(".cart-discount__discount-button");
        this.log("parent", pill);
        const discountCode = pill.dataset.discountCode;
        this.log("parent code", discountCode);
        if (!discountCode) return;
        const existingDiscounts = __privateMethod(this, _CartDiscount_instances, existingDiscounts_fn).call(this);
        const index = existingDiscounts.indexOf(discountCode);
        this.log("existing discount index", index);
        if (index === -1) return;
        existingDiscounts.splice(index, 1);
        const abortController = __privateMethod(this, _CartDiscount_instances, createAbortController_fn).call(this);
        __privateGet(this, _cartDrawer).disableCheckout();
        try {
          const config = fetchConfig("json", {
            body: JSON.stringify({ discount: existingDiscounts.join(","), sections: __privateGet(this, _cartDrawer).getSectionsToRender() })
          });
          const response = await fetch(Theme.routes.cart_update_url, {
            ...config,
            signal: abortController.signal
          });
          const data = await response.json();
          this.log(data);
          document.dispatchEvent(new DiscountUpdateEvent(data, { source: "cart-discount-component", sections: data.sections }));
        } catch (error2) {
        } finally {
          __privateSet(this, _activeFetch, null);
          __privateGet(this, _cartDrawer).enableCheckout();
        }
      });
    }
    // #debouncedOnFocusout = debounce(this.toggleForm, 1000).bind(this);
    connectedCallback() {
      super.connectedCallback();
      this.debug = false;
      this.loggerColor = "#BA6E0B";
      this.discountInput = this.querySelector("input");
      if (this.discountInput) {
        this.discountInput.addEventListener("input", __privateGet(this, _debouncedOnChange));
      }
    }
    /**
     * Handles updting submit button state when input starts to change
     * @param {*} event - input event
     */
    updateSubmit(event) {
      event.preventDefault();
      const input = this.querySelector(".cart-discount__input");
      const submitBtn = this.querySelector(".cart-discount__button");
      if ((input == null ? void 0 : input.value.length) == 0) {
        submitBtn == null ? void 0 : submitBtn.setAttribute("disabled", true);
      } else {
        submitBtn == null ? void 0 : submitBtn.removeAttribute("disabled");
      }
    }
    /**
     * Toggles form state between initial button trigger and form
     */
    toggleForm() {
      this.log("toggling form");
      const input = this.querySelector(".cart-discount__input");
      if (input.value.length > 0 || document.activeElement == input) return;
      const wrap = this.querySelector(".cart-discount__content");
      wrap.classList.toggle("initial");
    }
    /**
     * Shows discount form
     */
    showForm(event) {
      event.preventDefault();
      this.log("showing form");
      const wrap = this.querySelector(".cart-discount__content");
      wrap.classList.toggle("initial");
      const input = this.querySelector(".cart-discount__input");
      input.focus();
    }
  };
  _activeFetch = new WeakMap();
  _cartDrawer = new WeakMap();
  _debouncedOnChange = new WeakMap();
  _CartDiscount_instances = new WeakSet();
  createAbortController_fn = function() {
    if (__privateGet(this, _activeFetch)) {
      __privateGet(this, _activeFetch).abort();
    }
    const abortController = new AbortController();
    __privateSet(this, _activeFetch, abortController);
    return abortController;
  };
  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  handleDiscountError_fn = function(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === "discount_code" ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove("hidden");
    target.classList.remove("hidden");
  };
  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  existingDiscounts_fn = function() {
    const discountCodes = [];
    const discountPills = this.querySelectorAll(".cart-discount__discount-button");
    for (const pill of discountPills) {
      if (typeof pill.dataset.discountCode === "string") {
        discountCodes.push(pill.dataset.discountCode);
      }
    }
    return discountCodes;
  };
  function defineCartDiscount() {
    if (!customElements.get("cart-discount-component")) {
      customElements.define("cart-discount-component", CartDiscount);
    }
  }

  // src/scripts/_components/dialog.js
  var _handleResize, _previousScrollY, _DialogComponent_instances, handleClick_fn, handleKeyDown_fn;
  var DialogComponent = class extends Component {
    constructor(focusElement) {
      super();
      __privateAdd(this, _DialogComponent_instances);
      __publicField(this, "requiredRefs", ["dialog"]);
      __privateAdd(this, _handleResize, debounce(() => {
        const { minWidth, maxWidth } = this;
        if (!minWidth && !maxWidth) return;
        const windowWidth = window.innerWidth;
        if (windowWidth < minWidth || windowWidth > maxWidth) {
          this.closeDialog();
        }
      }, 50));
      __privateAdd(this, _previousScrollY, 0);
      /**
       * Closes the dialog.
       */
      __publicField(this, "closeDialog", async () => {
        const { dialog } = this.refs;
        if (!dialog.open) return;
        this.removeEventListener("click", __privateMethod(this, _DialogComponent_instances, handleClick_fn));
        this.removeEventListener("keydown", __privateMethod(this, _DialogComponent_instances, handleKeyDown_fn));
        dialog.style.animation = "none";
        void dialog.offsetWidth;
        dialog.classList.add("dialog-closing");
        dialog.style.animation = "";
        await onAnimationEnd(dialog, void 0, {
          subtree: false
        });
        document.documentElement.style.overflow = "";
        dialog.close();
        dialog.classList.remove("dialog-closing");
        dialog.classList.remove("open");
        this.removeTrapFocus();
        this.dispatchEvent(new DialogCloseEvent());
      });
      /**
       * Toggles the dialog.
       */
      __publicField(this, "toggleDialog", () => {
        if (this.refs.dialog.open) {
          this.closeDialog();
        } else {
          this.showDialog();
        }
      });
      this.focusElement = focusElement;
      this.trapFocusHandlers = {};
    }
    connectedCallback() {
      super.connectedCallback();
      if (this.minWidth || this.maxWidth) {
        window.addEventListener("resize", __privateGet(this, _handleResize));
      }
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.minWidth || this.maxWidth) {
        window.removeEventListener("resize", __privateGet(this, _handleResize));
      }
    }
    /**
     * Shows the dialog.
     */
    showDialog() {
      const { dialog } = this.refs;
      if (dialog.open) return;
      __privateSet(this, _previousScrollY, window.scrollY);
      requestAnimationFrame(() => {
        document.documentElement.style.overflow = "hidden";
        dialog.showModal();
        dialog.classList.add("open");
        this.dispatchEvent(new DialogOpenEvent());
        this.trapFocus(dialog);
        this.addEventListener("click", __privateMethod(this, _DialogComponent_instances, handleClick_fn));
        this.addEventListener("keydown", __privateMethod(this, _DialogComponent_instances, handleKeyDown_fn));
      });
    }
    /**
     * Handles focus trap for the current dialog
     */
    trapFocus(container, elementToFocus = container) {
      this.removeTrapFocus();
      this.trapFocusHandlers.keydown = (event) => {
        if (!event.code || event.code.toUpperCase() !== "TAB") return;
        const elements = this.getFocusableElements(container);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.target == last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }
        if ((event.target == container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };
      this.trapFocusHandlers.focusin = (event) => {
        const elements = this.getFocusableElements(container);
        const first = elements[0];
        if (!container.contains(event.target)) {
          event.preventDefault();
          first.focus();
          return;
        }
        container.addEventListener("keydown", this.trapFocusHandlers.keydown.bind(this));
      };
      this.trapFocusHandlers.focusout = function() {
        container.removeEventListener("keydown", this.trapFocusHandlers.keydown.bind(this));
      };
      container.addEventListener("focusout", this.trapFocusHandlers.focusout.bind(this));
      container.addEventListener("focusin", this.trapFocusHandlers.focusin.bind(this));
      elementToFocus.focus();
    }
    /**
     * Remove focus trap handlers
     */
    removeTrapFocus(elementToFocus = null) {
      this.focusElement.removeEventListener("focusin", this.trapFocusHandlers.focusin);
      this.focusElement.removeEventListener("focusout", this.trapFocusHandlers.focusout);
      this.focusElement.removeEventListener("keydown", this.trapFocusHandlers.keydown);
      if (elementToFocus) elementToFocus.focus();
    }
    /**
     * Gets and returns all focusable elements in the given container
     */
    getFocusableElements(container) {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
      ).filter((element) => {
        const tabindex = element.getAttribute("tabindex");
        return tabindex === null || parseInt(tabindex) >= 0;
      });
    }
    /**
     * Gets the minimum width of the dialog.
     *
     * @returns {number} The minimum width of the dialog.
     */
    get minWidth() {
      return Number(this.getAttribute("dialog-active-min-width"));
    }
    /**
     * Gets the maximum width of the dialog.
     *
     * @returns {number} The maximum width of the dialog.
     */
    get maxWidth() {
      return Number(this.getAttribute("dialog-active-max-width"));
    }
  };
  _handleResize = new WeakMap();
  _previousScrollY = new WeakMap();
  _DialogComponent_instances = new WeakSet();
  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  handleClick_fn = function(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };
  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown_fn = function(event) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this.closeDialog();
  };
  if (!customElements.get("dialog-component")) customElements.define("dialog-component", DialogComponent);
  var _DialogOpenEvent = class _DialogOpenEvent extends CustomEvent {
    constructor() {
      super(_DialogOpenEvent.eventName);
    }
  };
  __publicField(_DialogOpenEvent, "eventName", "dialog:open");
  var DialogOpenEvent = _DialogOpenEvent;
  var _DialogCloseEvent = class _DialogCloseEvent extends CustomEvent {
    constructor() {
      super(_DialogCloseEvent.eventName);
    }
  };
  __publicField(_DialogCloseEvent, "eventName", "dialog:close");
  var DialogCloseEvent = _DialogCloseEvent;
  document.addEventListener(
    "toggle",
    (event) => {
      if (event.target instanceof HTMLDetailsElement) {
        if (event.target.hasAttribute("scroll-lock")) {
          const { open } = event.target;
          if (open) {
            document.documentElement.setAttribute("scroll-lock", "");
          } else {
            document.documentElement.removeAttribute("scroll-lock");
          }
        }
      }
    },
    { capture: true }
  );

  // src/scripts/cart/components/cart-drawer.js
  var _sectionsToRender, _triggers, _handleCartAdd, _CartDrawer_instances, handleDialogChange_fn;
  var CartDrawer = class extends DialogComponent {
    constructor(focusElement) {
      super(focusElement);
      __privateAdd(this, _CartDrawer_instances);
      __privateAdd(this, _sectionsToRender, "cart-icon-bubble,cart-items");
      __publicField(this, "focusElement", this.querySelector("dialog"));
      __privateAdd(this, _triggers, document.querySelectorAll('[data-dialog-trigger="cart-drawer-component"]'));
      __privateAdd(this, _handleCartAdd, () => {
        if (!this.isOpen) {
          this.open();
        }
      });
    }
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener(CartAddEvent.eventName, __privateGet(this, _handleCartAdd));
      this.addEventListener(DialogOpenEvent.eventName, __privateMethod(this, _CartDrawer_instances, handleDialogChange_fn));
      this.addEventListener(DialogCloseEvent.eventName, __privateMethod(this, _CartDrawer_instances, handleDialogChange_fn));
      __privateGet(this, _triggers).forEach((trigger) => {
        trigger.addEventListener("click", this.open.bind(this));
      });
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      this.removeEventListener(CartAddEvent.eventName, __privateGet(this, _handleCartAdd));
      this.removeEventListener(DialogOpenEvent.eventName, __privateMethod(this, _CartDrawer_instances, handleDialogChange_fn));
      this.removeEventListener(DialogCloseEvent.eventName, __privateMethod(this, _CartDrawer_instances, handleDialogChange_fn));
      __privateGet(this, _triggers).forEach((trigger) => {
        trigger.removeEventListener("click", this.open.bind(this));
      });
    }
    disableCheckout() {
      this.log("disabling checkout");
      const submitButton = this.querySelector("#checkout");
      if (submitButton) {
        submitButton.setAttribute("disabled", true);
      }
    }
    enableCheckout() {
      this.log("enabling checkout");
      const submitButton = this.querySelector("#checkout");
      if (submitButton) {
        submitButton.removeAttribute("disabled");
      }
    }
    getSectionsToRender() {
      return __privateGet(this, _sectionsToRender);
    }
    open() {
      this.showDialog();
      this.isOpen = true;
      const cartBackgroundVideo = this.querySelectorAll(".cart-background-video");
      cartBackgroundVideo.forEach((video) => video.play());
      customElements.whenDefined("shopify-payment-terms").then(() => {
        var _a;
        const installmentsContent = (_a = document.querySelector("shopify-payment-terms")) == null ? void 0 : _a.shadowRoot;
        const cta = installmentsContent == null ? void 0 : installmentsContent.querySelector("#shopify-installments-cta");
        cta == null ? void 0 : cta.addEventListener("click", this.close, { once: true });
      });
    }
    close() {
      this.closeDialog();
      this.isOpen = false;
    }
  };
  _sectionsToRender = new WeakMap();
  _triggers = new WeakMap();
  _handleCartAdd = new WeakMap();
  _CartDrawer_instances = new WeakSet();
  handleDialogChange_fn = function(event) {
    if (event instanceof DialogOpenEvent) {
      this.isOpen = true;
    } else if (event instanceof DialogCloseEvent) {
      this.isOpen = false;
    }
  };
  function defineCartDrawer() {
    if (!customElements.get("cart-drawer-component")) {
      customElements.define("cart-drawer-component", CartDrawer);
    }
  }

  // src/scripts/cart/components/cart-items.js
  var _debouncedOnChange2, _cartDrawer2, _CartItemsComponent_instances, onQuantityChange_fn, _handleCartError, _handleCartUpdate, renderSections_fn, updateCartAttributes_fn, disableCartItems_fn, enableCartItems_fn, disableUpgradeBtns_fn, enableUpgradeBtns_fn, updateQuantitySelectors_fn, updateCartQuantitySelectorButtonStates_fn;
  var CartItemsComponent = class extends Component {
    constructor() {
      super(...arguments);
      __privateAdd(this, _CartItemsComponent_instances);
      __privateAdd(this, _debouncedOnChange2, debounce(__privateMethod(this, _CartItemsComponent_instances, onQuantityChange_fn), 300).bind(this));
      /** @type { CartDrawer | null} */
      __privateAdd(this, _cartDrawer2, this.closest("cart-drawer-component"));
      /**
       * Handles the discount update.
       * @param {DiscountUpdateEvent} event - The event.
       */
      __publicField(this, "handleDiscountUpdate", (event) => {
        __privateGet(this, _handleCartUpdate).call(this, event);
      });
      /**
       * Handles the cart error.
       * @param {number} line - The line.
       * @param {Object} parsedResponseText - The parsed response text.
       * @param {string} parsedResponseText.errors - The errors.
       */
      __privateAdd(this, _handleCartError, (line, parsedResponseText) => {
        const quantitySelector = this.refs.quantitySelectors[line - 1];
        const quantityInput = quantitySelector == null ? void 0 : quantitySelector.querySelector("input");
        if (!quantityInput) throw new Error("Quantity input not found");
        quantityInput.value = quantityInput.defaultValue;
        const cartItemError = this.refs[`cartItemError-${line}`];
        const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
        if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
        if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");
        cartItemError.textContent = parsedResponseText.errors;
        cartItemErrorContainer.classList.remove("hidden");
      });
      /**
       * Handles section rendering for all types of cart updates.
       *
       * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
       */
      __privateAdd(this, _handleCartUpdate, async (event) => {
        this.log("handleCartUpdate", event.detail);
        const { data, resource } = event.detail;
        const { source, sections } = data;
        if (!sections) {
          this.log("no sections to render");
          return;
        }
        __privateMethod(this, _CartItemsComponent_instances, renderSections_fn).call(this, sections);
        __privateMethod(this, _CartItemsComponent_instances, updateCartQuantitySelectorButtonStates_fn).call(this);
        await __privateMethod(this, _CartItemsComponent_instances, updateCartAttributes_fn).call(this, event);
      });
    }
    connectedCallback() {
      super.connectedCallback();
      this.debug = false;
      this.loggerColor = "#BA0B0B";
      this.swiperConfig = {
        slidesPerView: "auto",
        slidesPerGroup: 2,
        speed: 400,
        touchAngle: 75,
        threshold: 20,
        freeMode: {
          enabled: true,
          sticky: false
        },
        grabCursor: true,
        navigation: {
          nextEl: ".cart-recommendations-slider-next",
          prevEl: ".cart-recommendations-slider-prev"
        }
      };
      this.swiper = new Swiper(".cart-recommendations__slider", this.swiperConfig);
      document.addEventListener(ThemeEvents.cartUpdate, __privateGet(this, _handleCartUpdate));
      document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, __privateGet(this, _debouncedOnChange2));
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      document.removeEventListener(ThemeEvents.cartUpdate, __privateGet(this, _handleCartUpdate));
      document.removeEventListener(ThemeEvents.quantitySelectorUpdate, __privateGet(this, _debouncedOnChange2));
    }
    /**
     * Handles the line item removal.
     * @param {number} line - The line item index.
     */
    onLineItemRemove(line) {
      this.log("onLineItemRemove");
      this.updateQuantity({
        line,
        quantity: 0,
        action: "clear"
      });
      const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
      if (!cartItemRowToRemove) return;
      const { cartQuantity } = cartItemRowToRemove.dataset;
      const remove = () => cartItemRowToRemove.remove();
      if (prefersReducedMotion()) return cartItemRowToRemove.remove();
      cartItemRowToRemove.style.setProperty("--row-height", `${cartItemRowToRemove.clientHeight}px`);
      cartItemRowToRemove.classList.add("removing");
      this.calculateProgress(cartItemRowToRemove, 0, parseInt(cartQuantity));
      onAnimationEnd(cartItemRowToRemove, remove);
      return;
    }
    /**
     * Updates the quantity.
     * @param {Object} config - The config.
     * @param {number} config.line - The line.
     * @param {number} config.quantity - The quantity.
     * @param {string} config.action - The action.
     */
    updateQuantity(config) {
      this.log("updateQuantity");
      __privateMethod(this, _CartItemsComponent_instances, disableCartItems_fn).call(this);
      const { line, quantity } = config;
      const sectionsToRender = __privateGet(this, _cartDrawer2).getSectionsToRender();
      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToRender,
        sections_url: window.location.pathname
      });
      fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body })).then((response) => {
        return response.text();
      }).then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        if (parsedResponseText.errors) {
          __privateGet(this, _handleCartError).call(this, line, parsedResponseText);
          return;
        }
        __privateMethod(this, _CartItemsComponent_instances, updateQuantitySelectors_fn).call(this, parsedResponseText);
        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, {
            source: "cart-items-quantity-change",
            sections: parsedResponseText.sections
          })
        );
      }).catch((error2) => {
        console.error(error2);
      }).finally(() => {
        __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
      });
    }
    /**
     * Calculates new cart total using the given quantity delta and line
     * @param {number} line - The line that was updated
     * @param {number} quantity - The new line quantity
     * @param {number} originalQuantity - The original line quantity
     */
    calculateProgress(lineItemRow, quantity, originalQuantity) {
      let { linePrice } = lineItemRow.dataset;
      linePrice = parseInt(linePrice);
      if (isNaN(linePrice)) return;
      const progressUpdate = linePrice * (quantity - originalQuantity);
      this.triggerProgressAnimation(progressUpdate);
    }
    /**
     * Updates new progress bar percentage complete
     * @param {number} progressUpdate 
     * @returns object containing gift id and promo id if applicable
     */
    triggerProgressAnimation(progressUpdate) {
      this.log("triggerProgressAnimation");
      const progressBarElement = __privateGet(this, _cartDrawer2).querySelector(".cart-drawer__progress");
      if (!progressBarElement) return {};
      const progressBars = progressBarElement.querySelectorAll("[data-cart-progress]");
      if (!progressBars.length) return {};
      const maxBoundary = progressBarElement.querySelector("[data-boundary-max]");
      const { cartTotal } = progressBarElement.dataset;
      const { boundary, boundaryGift, promoId } = maxBoundary.dataset;
      const newTotal = parseInt(cartTotal) + progressUpdate;
      const giftCartItem = this.refs.cartItemRows.find((row) => row.dataset.progressAdd === "promoId");
      if (newTotal >= parseInt(boundary)) {
        progressBars.forEach((bar) => {
          bar.style.width = `100%`;
        });
        if (boundaryGift && !giftCartItem) {
          return { addGift: boundaryGift, promo: promoId };
        }
      } else {
        progressBars.forEach((bar) => {
          bar.style.width = `${Math.floor(newTotal / (parseInt(boundary) / 100))}%`;
        });
      }
      return {};
    }
    /**
     * Adds qualifying product for progress bar
     * @param {string} giftId - id of the gift to add
     * @param {string} promoId - promo id used for _progress_gift attribute
     */
    async addProgressBarGift(giftId, promoId = "true") {
      this.log("addProgressBarGift");
      let formData = {
        "items": [{
          "id": parseInt(giftId),
          "quantity": 1,
          "properties": {
            "_progress_gift": promoId ? promoId : "true"
          }
        }]
      };
      if (__privateGet(this, _cartDrawer2)) {
        formData.sections = __privateGet(this, _cartDrawer2).getSectionsToRender();
        formData.sections_url = "/cart";
      }
      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        });
        const data = await response.json();
        if (data.errors) {
          return;
        }
        this.dispatchEvent(new CartAddEvent(data, { source: "cart-progress-bar", sections: data.sections }));
      } catch (e) {
        console.error("Error:", e);
      }
    }
    /**
     * Checks cart contents to determine if a progress bar gift needs to be removed.
     * @param {Object} data.source - source of cart update api call
     * @param {Object} resource - response from cart update api call
     * @returns 
     */
    async validateCart(data, resource) {
      const { source } = data;
      const giftBoundaries = this.querySelectorAll("[data-boundary]");
      let { items, items_removed } = resource;
      let cartTotal = resource.total_price;
      if (source == "product-form") {
        try {
          const response = await fetch("/cart.js", {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json"
            }
          });
          const data2 = await response.json();
          items = data2.items;
          cartTotal = data2.total_price;
        } catch (e) {
          this.log("Could not get cart or update progress state", e);
        }
      }
      if (!items || !cartTotal) return;
      giftBoundaries.forEach((progressBoundary) => {
        const { boundary, promoId, boundaryGift } = progressBoundary.dataset;
        let giftJustRemoved = false;
        let giftLine = null;
        if (items_removed) {
          items_removed.forEach((removed) => {
            if (`${removed.variant_id}` === boundaryGift) {
              giftJustRemoved = true;
            }
          });
        }
        if (!boundaryGift || giftJustRemoved) {
          return;
        }
        this.log(boundaryGift, giftJustRemoved);
        items.forEach((item, i) => {
          if (item.properties["_progress_gift"] === promoId) {
            giftLine = i + 1;
          }
        });
        if (cartTotal < parseInt(boundary) && giftLine) {
          this.log("removing gift line:", giftLine);
          this.updateQuantity({
            line: giftLine,
            quantity: 0,
            action: "clear"
          });
        } else if (cartTotal >= parseInt(boundary) && !giftLine) {
          this.addProgressBarGift(boundaryGift, promoId);
        }
      });
    }
    /**
     * Replaces cart item row from event with given product variant and upgrades
     * to subscription
     * @param {*} event
     */
    async handleProductUpgrade(event) {
      this.log("upgrade line", event);
      __privateMethod(this, _CartItemsComponent_instances, disableCartItems_fn).call(this);
      __privateMethod(this, _CartItemsComponent_instances, disableUpgradeBtns_fn).call(this);
      const target = event.target;
      const { sellingPlan, productUpgrade } = target.dataset;
      const cartItemRow = target.closest(".cart-items__table-row");
      target.classList.add("loading");
      const { line, cartQuantity } = cartItemRow.dataset;
      const updates = {
        line: parseInt(line),
        quantity: 0
      };
      const formData = {
        items: [],
        sections: __privateGet(this, _cartDrawer2).getSectionsToRender()
      };
      formData.items.push({
        id: parseInt(productUpgrade),
        selling_plan: sellingPlan,
        quantity: cartQuantity
      });
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(updates)
        });
        const data = await response.json();
        if (data.status >= 400) {
          __privateGet(this, _handleCartError).call(this, parseInt(line), data);
          __privateMethod(this, _CartItemsComponent_instances, enableUpgradeBtns_fn).call(this);
          return;
        }
        const addResponse = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        });
        const addData = await addResponse.json();
        if (addData.status >= 400) {
          __privateGet(this, _handleCartError).call(this, parseInt(line), data);
          __privateMethod(this, _CartItemsComponent_instances, enableUpgradeBtns_fn).call(this);
          return;
        }
        this.dispatchEvent(
          new CartAddEvent(addData, {
            source: "cart-items-product-upgrade",
            sections: addData.sections
          })
        );
      } catch (e) {
        console.error("Could not upgrade set");
      } finally {
        __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
        __privateMethod(this, _CartItemsComponent_instances, enableUpgradeBtns_fn).call(this);
      }
    }
    /**
     * Updates selling plan on selling plan select change
     * @param {*} event 
     */
    updateSellingPlan(event) {
      this.log("selling plan update", event);
      const target = event.target;
      const [option] = target.selectedOptions;
      const { sellingPlan } = option.dataset || false;
      const cartItemRow = target.closest(".cart-items__table-row");
      this.setSellingPlan(cartItemRow, sellingPlan, false, __privateGet(this, _cartDrawer2).getSectionsToRender());
    }
    /**
     * Updates selling plan to default plan when on upgrade select
     * @param {*} event 
     */
    upgradeSellingPlan(event) {
      this.log("selling plan upgrade", event);
      const target = event.target;
      const { sellingPlan } = target.dataset || false;
      const cartItemRow = target.closest(".cart-items__table-row");
      const sectionsToRender = __privateGet(this, _cartDrawer2).getSectionsToRender();
      this.log(target, sellingPlan, cartItemRow, sectionsToRender);
      if (!sellingPlan) {
        this.log("no selling plan found");
        return;
      }
      target.classList.add("loading");
      this.setSellingPlan(cartItemRow, sellingPlan, target.dataset.welcomeKit, sectionsToRender);
    }
    /**
     * Sets the given selling plan for the given line
     * @param {*} line 
     * @param {*} sellingPlan 
     * @param {*} sections 
     */
    async setSellingPlan(cartItemRow, sellingPlan, upgradeWelcomeKit, sections) {
      var _a, _b;
      this.log("set selling plan");
      __privateMethod(this, _CartItemsComponent_instances, disableCartItems_fn).call(this);
      __privateMethod(this, _CartItemsComponent_instances, disableUpgradeBtns_fn).call(this);
      const { type, line, cartQuantity } = cartItemRow.dataset;
      const updates = {
        line: parseInt(line),
        quantity: cartQuantity,
        selling_plan: sellingPlan ? parseInt(sellingPlan) : null,
        sections,
        properties: {}
      };
      if (upgradeWelcomeKit) {
        updates.properties["_welcome_kit"] = "true";
      }
      try {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(updates)
        });
        let upgradeData = await response.json();
        this.log("upgradeData", upgradeData);
        if (upgradeData.status >= 400) {
          __privateGet(this, _handleCartError).call(this, parseInt(line), upgradeData);
          __privateMethod(this, _CartItemsComponent_instances, enableUpgradeBtns_fn).call(this);
          return;
        }
        if (!upgradeWelcomeKit) {
          this.dispatchEvent(
            new CartUpdateEvent(upgradeData, {
              source: "cart-items-selling-plan-selector",
              sections: upgradeData.sections
            })
          );
        } else {
          let formData = {
            items: [],
            sections
          };
          const samplePackInCart = (_a = upgradeData.items) == null ? void 0 : _a.find((item) => item.variant_id === 43630947926238);
          const carafeInCart = (_b = upgradeData.items) == null ? void 0 : _b.find((item) => item.variant_id === 43860529873118);
          if (!samplePackInCart) {
            formData.items.push({
              id: 43630947926238,
              quantity: 1
            });
          }
          if (!carafeInCart) {
            formData.items.push({
              id: 43860529873118,
              quantity: 1
            });
          }
          if (!samplePackInCart || !carafeInCart) {
            const addResponse = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify(formData)
            });
            const addData = await addResponse.json();
            this.dispatchEvent(
              new CartAddEvent(addData, {
                source: "cart-items-selling-plan-selector",
                sections: addData.sections
              })
            );
          } else {
            this.dispatchEvent(
              new CartUpdateEvent(upgradeData, {
                source: "cart-items-selling-plan-selector",
                sections: upgradeData.sections
              })
            );
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        __privateMethod(this, _CartItemsComponent_instances, enableCartItems_fn).call(this);
        __privateMethod(this, _CartItemsComponent_instances, enableUpgradeBtns_fn).call(this);
      }
    }
    /**
     * Calculate progress bar for quantity incentive box.
     * This is currently hardcoded for RTD launch
     * @param {*} quantityIncentive 
     */
    calculateQuantityIncentiveProgress(quantityIncentive, quantity) {
      if (!quantityIncentive) return;
      const progressBar = quantityIncentive.querySelector(".cart-items__upgrade-progress-bar-complete");
      const progressIcons = quantityIncentive.querySelectorAll(".cart-items__upgrade-progress-icons div");
      if (quantity === 1) {
        progressBar.style.width = "0%";
      } else if (quantity == 2) {
        progressBar.style.width = "50%";
      } else {
        progressBar.style.width = " 100%";
      }
      progressIcons.forEach((icon, index) => {
        if (index + 1 <= quantity) {
          icon.classList.add("complete");
        } else {
          icon.classList.remove("complete");
        }
      });
    }
    /**
     * Gets the section id.
     * @returns {string} The section id.
     */
    get sectionId() {
      const { sectionId } = this.dataset;
      if (!sectionId) throw new Error("Section id missing");
      return sectionId;
    }
  };
  _debouncedOnChange2 = new WeakMap();
  _cartDrawer2 = new WeakMap();
  _CartItemsComponent_instances = new WeakSet();
  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  onQuantityChange_fn = function(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line, originalQuantity } = event.detail;
    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }
    this.updateQuantity({
      line,
      quantity,
      action: "change"
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;
    this.calculateProgress(lineItemRow, quantity, originalQuantity);
    const quantityIncentive = lineItemRow.querySelector("[data-quantity-progress]");
    if (!quantityIncentive) return;
    this.calculateQuantityIncentiveProgress(quantityIncentive, quantity);
  };
  _handleCartError = new WeakMap();
  _handleCartUpdate = new WeakMap();
  renderSections_fn = function(sections) {
    this.log("rendering sections");
    this.swiper.destroy();
    Object.keys(sections).forEach((key) => {
      const sectionElements = document.querySelectorAll(`[data-rehydration-key="${key}"]`);
      if (sectionElements.length > 0 && sections[key]) {
        sectionElements.forEach((sectionElement) => {
          sectionElement.innerHTML = new DOMParser().parseFromString(sections[key], "text/html").getElementById(key).innerHTML;
        });
      }
    });
    this.swiper = new Swiper(".cart-recommendations__slider", this.swiperConfig);
    const cartBackgroundVideo = this.querySelectorAll(".cart-background-video");
    cartBackgroundVideo.forEach((video) => video.play());
  };
  updateCartAttributes_fn = async function(event) {
    var _a, _b;
    this.log("update cart attributes");
    const hasWelcomeKitDiscount = ((_b = (_a = this.querySelector("[data-cart-welcome-kit]")) == null ? void 0 : _a.dataset) == null ? void 0 : _b.cartWelcomeKit) === "true";
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          attributes: {
            "_welcome_kit": hasWelcomeKitDiscount
          }
        })
      });
    } catch (e) {
      console.error("Error updating cart attributes:", error);
    }
  };
  /**
   * Disables the cart items.
   */
  disableCartItems_fn = function() {
    this.classList.add("cart-items-disabled");
    __privateGet(this, _cartDrawer2).disableCheckout();
  };
  /**
   * Enables the cart items.
   */
  enableCartItems_fn = function() {
    this.classList.remove("cart-items-disabled");
    __privateGet(this, _cartDrawer2).enableCheckout();
  };
  disableUpgradeBtns_fn = function() {
    const upgradeCTAs = this.querySelectorAll("button.cart-items__upgrade-cta");
    upgradeCTAs.forEach((btn) => {
      btn.setAttribute("disabled", true);
    });
  };
  enableUpgradeBtns_fn = function() {
    const upgradeCTAs = this.querySelectorAll("button.cart-items__upgrade-cta");
    upgradeCTAs.forEach((btn) => {
      btn.removeAttribute("disabled");
    });
  };
  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  updateQuantitySelectors_fn = function(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;
        input.setAttribute("data-cart-quantity", item.quantity.toString());
        if ("updateCartQuantity" in selector && typeof selector.updateCartQuantity === "function") {
          selector.updateCartQuantity();
        }
      }
    }
  };
  /**
   * Updates button states for all cart quantity selector components.
   */
  updateCartQuantitySelectorButtonStates_fn = function() {
    var _a;
    for (const selector of document.querySelectorAll("cart-quantity-selector-component")) {
      (_a = selector.updateButtonStates) == null ? void 0 : _a.call(selector);
    }
  };
  function defineCartItems() {
    if (!customElements.get("cart-items-component")) {
      customElements.define("cart-items-component", CartItemsComponent);
    }
  }

  // src/scripts/_components/quantity-selector.js
  var QuantitySelectorComponent = class extends Component {
    constructor() {
      super(...arguments);
      __publicField(this, "requiredRefs", ["quantityInput", "minusButton", "plusButton"]);
      __publicField(this, "serverDisabledMinus", false);
      __publicField(this, "serverDisabledPlus", false);
      __publicField(this, "initialized", false);
    }
    connectedCallback() {
      this.log("connected");
      super.connectedCallback();
      const { minusButton, plusButton } = this.refs;
      if (minusButton.disabled) {
        this.serverDisabledMinus = true;
      }
      if (plusButton.disabled) {
        this.serverDisabledPlus = true;
      }
      this.initialized = true;
      this.updateButtonStates();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
    }
    /**
     * Updates cart quantity and refreshes component state
     * @param {number} cartQty - The quantity currently in cart for this variant
     */
    setCartQuantity(cartQty) {
      this.refs.quantityInput.setAttribute("data-cart-quantity", cartQty.toString());
      this.updateCartQuantity();
    }
    /**
     * Checks if the current quantity can be added to cart without exceeding max
     * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
     */
    canAddToCart() {
      const { max, cartQuantity, value } = this.getCurrentValues();
      const quantityToAdd = value;
      const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;
      return {
        canAdd: !wouldExceedMax,
        maxQuantity: max,
        cartQuantity,
        quantityToAdd
      };
    }
    /**
     * Gets the current quantity value
     * @returns {string} The current value
     */
    getValue() {
      return this.refs.quantityInput.value;
    }
    /**
     * Sets the current quantity value
     * @param {string} value - The value to set
     */
    setValue(value) {
      this.refs.quantityInput.value = value;
    }
    /**
     * Updates min/max/step constraints and snaps value to valid increment
     * @param {string} min - Minimum value
     * @param {string|null} max - Maximum value (null if no max)
     * @param {string} step - Step increment
     */
    updateConstraints(min, max, step) {
      const { quantityInput } = this.refs;
      const currentValue = parseInt(quantityInput.value) || 0;
      quantityInput.min = min;
      if (max) {
        quantityInput.max = max;
      } else {
        quantityInput.removeAttribute("max");
      }
      quantityInput.step = step;
      const newMin = parseIntOrDefault(min, 1);
      const newStep = parseIntOrDefault(step, 1);
      const effectiveMax = this.getEffectiveMax();
      let newValue = currentValue;
      if ((currentValue - newMin) % newStep !== 0) {
        newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
      }
      newValue = Math.max(newMin, Math.min(effectiveMax != null ? effectiveMax : Infinity, newValue));
      if (newValue !== currentValue) {
        quantityInput.value = newValue.toString();
      }
      this.updateButtonStates();
    }
    /**
     * Gets current values from DOM (fresh read every time)
     * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
     */
    getCurrentValues() {
      const { quantityInput } = this.refs;
      return {
        min: parseIntOrDefault(quantityInput.min, 1),
        max: parseIntOrDefault(quantityInput.max, null),
        step: parseIntOrDefault(quantityInput.step, 1),
        value: parseIntOrDefault(quantityInput.value, 0),
        cartQuantity: parseIntOrDefault(quantityInput.getAttribute("data-cart-quantity"), 0)
      };
    }
    /**
     * Gets the effective maximum value for this quantity selector
     * Product page: max - cartQuantity (how many can be added)
     * Override in subclass for different behavior
     * @returns {number | null} The effective max, or null if no max
     */
    getEffectiveMax() {
      const { max, cartQuantity, min } = this.getCurrentValues();
      if (max === null) return null;
      return Math.max(max - cartQuantity, min);
    }
    /**
     * Updates button states based on current value and limits
     */
    updateButtonStates() {
      const { minusButton, plusButton } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      if (!this.serverDisabledMinus) {
        minusButton.disabled = value <= min;
      }
      if (!this.serverDisabledPlus) {
        plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
      }
    }
    /**
     * Updates quantity by a given step
     * @param {number} stepMultiplier - Positive for increase, negative for decrease
     */
    updateQuantity(stepMultiplier) {
      const { quantityInput } = this.refs;
      const { min, step, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      const newValue = Math.min(effectiveMax != null ? effectiveMax : Infinity, Math.max(min, value + step * stepMultiplier));
      quantityInput.value = newValue.toString();
      this.onQuantityChange();
      this.updateButtonStates();
    }
    /**
     * Handles the quantity increase event.
     * @param {Event} event - The event.
     */
    increaseQuantity(event) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.updateQuantity(1);
    }
    /**
     * Handles the quantity decrease event.
     * @param {Event} event - The event.
     */
    decreaseQuantity(event) {
      if (!(event.target instanceof HTMLElement)) return;
      event.preventDefault();
      this.updateQuantity(-1);
    }
    /**
     * When our input gets focused, we want to fully select the value.
     * @param {FocusEvent} event
     */
    selectInputValue(event) {
      const { quantityInput } = this.refs;
      if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
      quantityInput.select();
    }
    /**
     * Handles the quantity set event (on blur).
     * Validates and snaps to valid values.
     * @param {Event} event - The event.
     */
    setQuantity(event) {
      if (!(event.target instanceof HTMLInputElement)) return;
      event.preventDefault();
      const { quantityInput } = this.refs;
      const { min, step } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      const quantity = Math.min(effectiveMax != null ? effectiveMax : Infinity, Math.max(min, parseInt(event.target.value) || 0));
      if ((quantity - min) % step !== 0) {
        quantityInput.value = quantity.toString();
        quantityInput.reportValidity();
        return;
      }
      quantityInput.value = quantity.toString();
      this.onQuantityChange();
      this.updateButtonStates();
    }
    /**
     * Handles the quantity change event.
     */
    onQuantityChange() {
      const { quantityInput } = this.refs;
      const newValue = parseInt(quantityInput.value);
      const originalValue = parseInt(quantityInput.dataset.cartQuantity);
      this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || void 0, originalValue));
    }
    /**
     * Updates the cart quantity from data attribute and refreshes button states
     * Called when cart is updated from external sources
     */
    updateCartQuantity() {
      const { quantityInput } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      const clampedValue = Math.min(effectiveMax != null ? effectiveMax : Infinity, Math.max(min, value));
      if (clampedValue !== value) {
        quantityInput.value = clampedValue.toString();
      }
      this.updateButtonStates();
    }
    /**
     * Gets the quantity input.
     * @returns {HTMLInputElement} The quantity input.
     */
    get quantityInput() {
      if (!this.refs.quantityInput) {
        throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
      }
      return this.refs.quantityInput;
    }
  };
  if (!customElements.get("quantity-selector-component")) {
    customElements.define("quantity-selector-component", QuantitySelectorComponent);
  }

  // src/scripts/cart/components/cart-quantity-selector.js
  var CartQuantitySelector = class extends QuantitySelectorComponent {
    /**
     * Gets the effective maximum value for cart quantity selector
     * Cart page: uses absolute max (how much can be in cart total)
     * @returns {number | null} The effective max, or null if no max
     */
    getEffectiveMax() {
      const { max } = this.getCurrentValues();
      return max;
    }
    /**
     * Updates button states based on current value and limits
     * Cart buttons are always managed client-side, never server-disabled
     */
    updateButtonStates() {
      const { minusButton, plusButton } = this.refs;
      const { min, value } = this.getCurrentValues();
      const effectiveMax = this.getEffectiveMax();
      minusButton.disabled = value <= min;
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  };
  function defineCartQuantitySelector() {
    if (!customElements.get("cart-quantity-selector")) {
      customElements.define("cart-quantity-selector", CartQuantitySelector);
    }
  }

  // src/scripts/cart/cart-default.js
  defineCartDrawer();
  defineCartItems();
  defineCartDiscount();
  defineCartQuantitySelector();
})();
